import { LRUCache } from 'lru-cache'
import { format } from 'pretty-format'

/**
 * When creating a cache key, we want it to be as accurate as possible. However,
 * we cannot make it {@link Infinity} since that can lead to infinite loops when
 * there are circular references. Therefore, we need to limit the depth of the
 * cache key, but not so much that we end up with collisions.
 */
const CACHE_KEY_MAX_DEPTH = 10

export class Cache<TKey, TVal extends NonNullable<unknown>> {
  private static readonly _assignedKeyProp = Symbol.for(
    '@attain-apps/data-utils.Cache.key',
  )

  /**
   * Wraps the given function such that its results are cached so if the
   * function is called multiple times with the same arguments, the result from
   * the first invocation will be reused
   *
   * @example
   * const cachedGet = Cache.fn(expensiveGet)
   * cacheGet('item1') // Calls expensiveGet
   * cacheGet('item1') // Returns result from cache
   * cacheGet('item1') // Returns result from cache
   */
  public static fn<TFn extends (...args: any) => any>(fn: TFn): TFn
  public static fn(fn: Function): Function {
    const cache = new Cache()
    return (...args: readonly unknown[]) =>
      cache.produce(args, () => fn(...args))
  }

  /**
   * Adds a hidden property to the given object which will be used by
   * {@link stringifyKey} to return the key assigned here
   */
  public static attachKey(obj: unknown, key: () => unknown): void {
    Object.defineProperty(obj, this._assignedKeyProp, { get: key })
  }

  /**
   * Stores the actual contents of this {@link Cache}
   */
  private readonly _lruCache: LRUCache<string, TVal>

  /** Used by {@link stringifyKey} */
  private readonly _fnIds = new WeakMap<Function, number>()
  /** Used by {@link stringifyKey} */
  private _nextFnId = 1

  constructor({
    maxItems = Cache.constructor.Options.DEFAULT.maxItems,
  }: Cache.constructor.Options = {}) {
    this._lruCache = new LRUCache({ max: maxItems })
  }

  /**
   * Produces an item from the {@link Cache}, by retrieving the associated item
   * if it exists, or creating it if it does not
   */
  public produce(key: TKey): TVal | undefined
  public produce(key: TKey, producer: () => TVal): TVal
  public produce(key: unknown, producer?: () => TVal): TVal | undefined {
    const keyStr = this.stringifyKey(key)
    const cached = this._lruCache.get(keyStr)
    if (cached !== undefined || !producer) {
      // If a cached entry was found, or no producer was provided, then we
      // return what we have
      return cached
    }
    // Otherwise, we produce a new value, store it, and return it
    const val = producer()
    this._lruCache.set(keyStr, val)
    return val
  }

  /**
   * Creates a string that represents a given key
   *
   * This attempts to represent an object accurately, but avoids collisions when
   * caching might not be desired (e.g. with closures)
   */
  public stringifyKey(key: unknown): string {
    return format(key, {
      maxDepth: CACHE_KEY_MAX_DEPTH,
      min: true,
      plugins: [
        {
          // Uses an assigned key, if there is one
          serialize: (val, config, indentation, depth, refs, printer) =>
            printer(
              val[Cache._assignedKeyProp],
              config,
              indentation,
              depth,
              refs,
            ),
          test: (val: unknown) =>
            val !== undefined &&
            val !== null &&
            Object.hasOwn(val, Cache._assignedKeyProp),
        },
        {
          // Handles problem where closures (functions that reference local
          // variables) are treated as if they are the same, even when they
          // reference different local variables
          print: val => {
            // NOTE: this was determined by the `test` function
            const fn = val as Function
            let fnId = this._fnIds.get(fn)
            if (fnId === undefined) {
              fnId = this._nextFnId++
              this._fnIds.set(fn, fnId)
            }
            return `Fn<${fnId}>`
          },
          test: val => typeof val === 'function',
        },
      ],
    })
  }
}
export namespace Cache {
  export namespace constructor {
    export interface Options {
      /**
       * @default 100
       */
      maxItems?: number
    }
    export namespace Options {
      export const DEFAULT: Readonly<Required<Options>> = {
        maxItems: 100,
      }
    }
  }
}
